import InsightLayout from "../../components/InsightLayout";
import FoerderHinweis from "../../components/FoerderHinweis";

const SUBTITLE = `RAG — Retrieval-Augmented Generation — ist die meistverkaufte und am häufigsten falsch eingesetzte KI-Architektur im Mittelstand. Richtig gemacht, löst sie das Halluzinations- und Aktualitätsproblem von LLMs und liefert auditierbare Antworten aus Ihren eigenen Dokumenten. Falsch gemacht, ist sie teurer Aufwand für ein Problem, das ein simples Kontextfenster oder eine Stichwortsuche besser gelöst hätte. Hier ist die ehrliche Entscheidungsmatrix — inklusive eines Rechenbeispiels, das zeigt, wann RAG 97 % der Kosten spart und wann es schlicht überflüssig ist.`;

const RagImMittelstand = () => {
  return (
    <InsightLayout
      title="RAG im Mittelstand: Wann sich eine Vektordatenbank lohnt — und wann nicht"
      subtitle={SUBTITLE}
      metaDescription="Wann sich eine Vektordatenbank lohnt und wann ein Kontextfenster reicht: die ehrliche Entscheidungsmatrix inklusive Rechenbeispiel für den Mittelstand."
      publishedDate="2026-05-26"
      readingTime="12 Min Lesezeit"
      category="AI Infrastructure"
    >
      <p className="text-lg">
        RAG ist das Akronym, das Ihnen aktuell jeder KI-Anbieter verkaufen
        will. Retrieval-Augmented Generation. Es ist eine exzellente
        Technologie — und gleichzeitig die am häufigsten unnötig eingesetzte.
        Ich habe Projekte gesehen, in denen ein aufwendiges RAG-System gebaut
        wurde für ein Problem, das eine simple Stichwortsuche besser gelöst
        hätte. Und andere, in denen RAG genau richtig war und 97 % der Kosten
        sparte.
      </p>

      <p>
        Dieser Artikel erklärt, was RAG ist, wann es sich lohnt — und vor
        allem, wann nicht. Die „Wann nicht"-Frage ist die wertvollere, weil
        sie Sie vor Fehlinvestitionen schützt.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Was RAG ist — das Open-Book-Prinzip
      </h2>

      <p>
        Ein LLM hat zwei strukturelle Schwächen (ausführlich im{" "}
        <a
          href="/insights/llms-erklaert-fuer-entscheider"
          className="text-[#C5A66A] underline"
        >
          Artikel über die Grenzen von LLMs
        </a>
        ): Es weiß nichts nach seinem Trainings-Cutoff, und es halluziniert,
        wenn ihm Fakten fehlen. RAG löst beides mit einem einfachen Prinzip:
        Statt das Modell aus dem Gedächtnis antworten zu lassen, gibt man ihm
        die relevanten Dokumente <em>vor</em> der Antwort mit — wie eine
        Klausur, in der das Lehrbuch erlaubt ist.
      </p>

      <p>
        Konkret: Die Frage des Nutzers wird genutzt, um in einer Dokumenten-
        Sammlung die relevantesten Passagen zu finden. Diese Passagen werden
        zusammen mit der Frage an das LLM gegeben, das daraus die Antwort
        formuliert. Drei Vorteile:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>Aktualität:</strong> Neue Dokumente werden einfach in die
          Sammlung eingepflegt, kein teures Nachtrainieren des Modells.
        </li>
        <li>
          <strong>Faktentreue:</strong> Mit der Anweisung „antworte nur auf
          Basis der bereitgestellten Dokumente" wird das Modell von der
          kreativen Schreibmaschine zur präzisen Synthese-Einheit.
        </li>
        <li>
          <strong>Auditierbarkeit:</strong> Jede Aussage lässt sich auf das
          Quelldokument zurückführen — entscheidend für ISO-Zertifizierungen,
          Audits und Haftungsfragen im Mittelstand.
        </li>
      </ul>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Wie es funktioniert — fünf Schritte
      </h2>

      <p>
        Sie müssen das nicht selbst bauen, aber Sie sollten die Kette
        verstehen, um Anbieter zu prüfen:
      </p>

      <ol className="list-decimal pl-6 space-y-2 my-4">
        <li>
          <strong>Chunking:</strong> Dokumente werden in sinnvolle Abschnitte
          zerlegt. Der häufigste Fehler liegt hier — wer Texte stur nach
          Zeichenzahl zerschneidet, zerstört den Sinn. Gut gemacht wird entlang
          logischer Grenzen (Kapitel, Abschnitte) geschnitten.
        </li>
        <li>
          <strong>Embedding:</strong> Jeder Abschnitt wird in eine Zahlenreihe
          übersetzt, die seine Bedeutung repräsentiert. Ähnliche Bedeutungen
          („Rechnung" und „Faktura") landen mathematisch nah beieinander.
        </li>
        <li>
          <strong>Vector Store:</strong> Diese Zahlenreihen werden in einer
          spezialisierten Datenbank gespeichert, die in Millisekunden die
          ähnlichsten findet.
        </li>
        <li>
          <strong>Retrieval:</strong> Zur Nutzerfrage werden die passendsten
          Abschnitte gefunden. Die besten Systeme kombinieren hier semantische
          Suche mit klassischer Stichwortsuche (Hybrid Search) und sortieren
          das Ergebnis mit einem Reranking-Schritt nach — der stärkste
          Qualitätshebel überhaupt.
        </li>
        <li>
          <strong>Generation:</strong> Die Top-Abschnitte gehen mit der Frage
          ans LLM, das die Antwort formuliert.
        </li>
      </ol>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die Vektordatenbank-Wahl — mit DSGVO-Brille
      </h2>

      <p>
        Für den Mittelstand ist die Wahl der Vektordatenbank vor allem eine
        Datenschutz- und Betriebskosten-Entscheidung:
      </p>

      <div className="overflow-x-auto my-8">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-[#0F1B2D] text-white">
            <tr>
              <th className="px-4 py-3 text-left">Lösung</th>
              <th className="px-4 py-3 text-left">DSGVO</th>
              <th className="px-4 py-3 text-left">Einordnung</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="px-4 py-3 font-medium">pgvector (PostgreSQL)</td>
              <td className="px-4 py-3">✅ perfekt</td>
              <td className="px-4 py-3">
                Die pragmatischste Lösung. Läuft als Erweiterung Ihrer
                bestehenden PostgreSQL-DB — kein neues Silo, keine
                Lizenzkosten. Ideal unter ~10 Mio. Abschnitten.
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">Qdrant</td>
              <td className="px-4 py-3">✅ sehr gut</td>
              <td className="px-4 py-3">
                Performance- und Kostensieger bei dedizierten Vektor-DBs.
                DSGVO-konformes EU-Hosting verfügbar, auch self-hosted.
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">Weaviate</td>
              <td className="px-4 py-3">✅ gut</td>
              <td className="px-4 py-3">
                Stark bei komplexen Datenmodellen, EU-Hosting + self-hosted
                möglich.
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">Pinecone</td>
              <td className="px-4 py-3">⚠️ komplexer</td>
              <td className="px-4 py-3">
                Gut für schnelle Prototypen. US-Anbieter — EU-Region zwar
                buchbar, aber als US-Unternehmen CLOUD-Act-exponiert; plus
                potenziell hohe laufende Fixkosten.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Faustregel für den Mittelstand: Wenn Sie ohnehin PostgreSQL nutzen,
        starten Sie mit <strong>pgvector</strong>. Sie brauchen kein neues
        System, keine neue Lizenz, keinen neuen Datenschutz-Vertrag. Erst wenn
        Sie an dessen Grenzen stoßen, lohnt der Wechsel zu einer dedizierten
        Lösung wie Qdrant.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Der Anti-Hype-Kern: Wann RAG NICHT die Antwort ist
      </h2>

      <p>
        Hier ist der Teil, den Ihnen kein RAG-Anbieter erzählt. Es gibt drei
        Situationen, in denen RAG der falsche Weg ist:
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        1. RAG vs. großes Kontextfenster
      </h3>

      <p>
        Moderne Modelle wie Gemini halten über eine Million Token im Kontext —
        warum also Dokumente aufwendig zerschneiden, statt einfach alles
        reinzugeben? Drei Gründe sprechen trotzdem oft für RAG:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>Der „Lost in the Middle"-Effekt:</strong> Modelle können
          riesige Kontexte lesen, übersehen aber statistisch belegbar Details,
          die in der Mitte vergraben sind. RAG filtert das Rauschen vorab.
        </li>
        <li>
          <strong>Latenz:</strong> Eine Million Token zu verarbeiten dauert
          30-60 Sekunden. RAG antwortet in 1-2 Sekunden.
        </li>
        <li>
          <strong>Kosten:</strong> Hier wird es drastisch — siehe Rechen-
          beispiel unten.
        </li>
      </ul>

      <p>
        <strong>Aber:</strong> Bei kleinen, einmaligen Aufgaben (ein einzelnes
        50-Seiten-Dokument analysieren) ist das große Kontextfenster einfacher
        und besser. RAG lohnt sich erst bei wiederkehrenden Abfragen auf große,
        wachsende Dokumentenbestände.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        2. RAG vs. simple Stichwortsuche
      </h3>

      <p>
        Wenn Ihre Nutzer nach exakten Begriffen suchen — Artikelnummern,
        Seriennummern, Kunden-IDs, standardisierte Bezeichnungen („Ersatzteil
        X-400") — dann ist eine klassische Stichwortsuche schneller, billiger
        und zuverlässiger. RAG ist erst dann zwingend, wenn nach{" "}
        <em>Konzepten</em> gesucht wird, deren exakte Worte nicht in der
        Anfrage stehen („Welche unserer Pumpen sind säureresistent?") — und
        wenn eine zusammenhängende Antwort statt einer Linkliste gebraucht
        wird.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        3. RAG vs. Fine-Tuning
      </h3>

      <p>
        Ein verbreiteter Irrtum: „Wir trainieren dem Modell unser Firmenwissen
        an." Das ist fast immer falsch. <strong>Fine-Tuning dient der
        Anpassung von Verhalten, Stil und Tonalität — nicht der Vermittlung von
        Fakten.</strong> Wenn das Modell Ihre aktuellen Preise, Verträge oder
        Handbücher kennen soll, ist RAG der richtige Weg, nicht Fine-Tuning.
        Wer Fakten ins Modell trainiert, bekommt teure, schwer aktualisierbare
        und trotzdem halluzinationsanfällige Ergebnisse.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Das Rechenbeispiel, das alles klärt
      </h2>

      <p>
        Nehmen wir ein realistisches Szenario: 50 Anfragen pro Tag auf eine
        große Dokumentensammlung. Größenordnung der monatlichen Kosten (Stand
        2026, gerundet):
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>Ohne RAG (alles ins Kontextfenster):</strong> Jede Anfrage
          lädt das gesamte Dokumenten-Volumen. Bei ~1.500 Anfragen/Monat landen
          Sie schnell im Bereich von <strong>mehreren tausend Euro pro
          Monat</strong> — nur für die Modell-Kosten.
        </li>
        <li>
          <strong>Mit RAG:</strong> Jede Anfrage lädt nur die relevanten
          Abschnitte (statt aller Dokumente). Die Modell-Kosten fallen um über
          90 %, dazu kommen moderate Kosten für die Vektordatenbank. Sie landen
          im Bereich von <strong>einigen hundert Euro pro Monat</strong>.
        </li>
      </ul>

      <p>
        Die Ersparnis kann je nach Volumen über 90 % betragen. Das ist der Grund,
        warum RAG bei wiederkehrenden Abfragen auf große Bestände fast immer
        gewinnt — und warum es bei kleinen, einmaligen Aufgaben überflüssiger
        Aufwand ist. Die Volumen-Schwelle ist die eigentliche
        Entscheidungsgröße.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die häufigsten Fehler im Mittelstand
      </h2>

      <ul className="list-disc pl-6 space-y-3 my-4">
        <li>
          <strong>„Garbage in, garbage out":</strong> Der Versuch, ein
          chaotisches Netzlaufwerk mit Dubletten und veralteten Dateien aus
          2012 unbereinigt zu indexieren. Ergebnis: Die KI zitiert
          selbstbewusst veraltete Preislisten und abgelaufene Verträge. Die
          Datenbereinigung VOR dem Aufbau ist kein optionaler Schritt.
        </li>
        <li>
          <strong>Fehlende Metadaten:</strong> Abschnitte ohne Information,
          aus welchem Dokument und welcher Version sie stammen. Ohne Metadaten
          ist die Auditierbarkeit — der halbe Wert von RAG — dahin.
        </li>
        <li>
          <strong>Optimierung „nach Gefühl":</strong> Anpassungen werden an
          drei Beispielfragen getestet. Professionelle RAG-Projekte nutzen
          Mess-Frameworks (etwa RAGAS), die mathematisch prüfen, ob die
          Antworten wirklich auf den Quellen basieren und relevant sind.
        </li>
      </ul>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Realistischer Aufwand — mit Gates statt Bauchgefühl
      </h2>

      <p>
        Für ein mittelständisches Szenario (Größenordnung: zehntausende
        Dokumente, hunderte Nutzer) liegen die einmaligen Aufbaukosten
        realistisch im <strong>mittleren fünfstelligen Bereich</strong>
        (Konzept, Datenbereinigung, Integration in Ihr Rechtekonzept), die
        laufenden Betriebskosten im Bereich einiger hundert bis gut tausend
        Euro pro Monat. Steuern Sie das Projekt über Qualitäts-Gates, nicht
        über Kalendertage:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>Gate 1:</strong> Vektorisierung erst, wenn das Daten-Audit
          abgeschlossen und Dubletten substanziell bereinigt sind.
        </li>
        <li>
          <strong>Gate 2:</strong> Testbetrieb erst, wenn die Faktentreue auf
          einem repräsentativen Testset von ~100 Fragen verlässlich hoch ist
          (messbar via RAGAS).
        </li>
        <li>
          <strong>Gate 3:</strong> Produktivstart erst nach DSGVO-Audit und
          implementiertem Berechtigungskonzept (wer darf welche Dokumente
          sehen — die KI muss diese Rechte respektieren).
        </li>
      </ul>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die Quintessenz
      </h2>

      <p>
        RAG ist exzellent, wenn Sie wiederkehrende, konzeptuelle Fragen auf
        einen großen, wachsenden Dokumentenbestand beantworten müssen und
        Auditierbarkeit brauchen. Es ist überflüssig bei einmaligen Aufgaben,
        exakten Stichwortsuchen oder kleinen Dokumentenmengen, die ins
        Kontextfenster passen.
      </p>

      <p>
        Die ehrlichste Beratung, die ich einem Mittelständler geben kann,
        lautet daher oft: <em>Bevor wir über RAG reden, lassen Sie uns prüfen,
        ob Sie es überhaupt brauchen.</em> In etwa einem Drittel der Fälle ist
        die Antwort Nein — und das gesparte Budget ist besser in
        Datenbereinigung oder Prompt-Kompetenz investiert. Genau diese
        Ehrlichkeit unterscheidet einen Berater von einem Verkäufer.
      </p>

      <p className="mt-12 text-sm text-gray-600 italic">
        Stand: 26. Mai 2026. Kosten sind Größenordnungen, keine Angebote — die
        konkreten Zahlen hängen von Volumen, Anbieter und Architektur ab.
        Primäre Quellen:{" "}
        <a
          href="https://qdrant.tech"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          qdrant.tech
        </a>{" "}
        ·{" "}
        <a
          href="https://github.com/pgvector/pgvector"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          pgvector
        </a>{" "}
        ·{" "}
        <a
          href="https://docs.ragas.io"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          RAGAS
        </a>{" "}
        ·{" "}
        <a
          href="https://cohere.com"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          cohere.com
        </a>
        . Verwandte Artikel:{" "}
        <a
          href="/insights/open-weight-sommer-2026"
          className="text-[#C5A66A] underline"
        >
          Der Open-Weight-Sommer
        </a>{" "}
        ·{" "}
        <a
          href="/insights/open-source-vs-frontier"
          className="text-[#C5A66A] underline"
        >
          Open Source vs. Frontier
        </a>
        . Recherche-Unterstützung durch Multi-Agent-Tooling, finale
        Darstellung und Einordnung redaktionell.
      </p>

      <FoerderHinweis
        text="RAG-Projekte fallen förderrechtlich unter Digitalisierung: Software, externe Implementierung und teils Cloud-Kosten sind je nach Bundesland zuschussfähig. Das entschärft genau die Budget-Frage aus dem Rechenbeispiel oben — sofern der Antrag vor Projektbeginn gestellt wird."
        links={[
          { href: "https://foerdermittel.daaniyalkhan.com/programme/", label: "Förderprogramme nach Bundesland" },
          { href: "https://foerdermittel.daaniyalkhan.com/wissen/digitalisierung-foerdern/", label: "Guide: Digitalisierung fördern" },
          { href: "https://foerdermittel.daaniyalkhan.com/", label: "Kostenloser Förder-Check" },
        ]}
      />
    </InsightLayout>
  );
};

export default RagImMittelstand;
